import { useFormContext } from "react-hook-form";
import {
  Field,
  FormFieldStatus,
  Hint,
  Input,
  Label,
} from "@gocardless/flux-react";
import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useEffect } from "react";

import { SignupFormData } from "../../consts";
import { TrackingErrorOrigin } from "../../../consts";

import { useSegment } from "src/technical-integrations/segment/useSegment";
import { TrackingEvent } from "src/common/trackingEvents";
import {
  getInputErrorStatus,
  invalidGoCardlessEmailSignUp,
  VALID_EMAIL_REGEX,
} from "src/utils/inputValidation";

export const EmailField: React.FC = () => {
  const { sendEvent } = useSegment();
  const { i18n } = useLingui();
  const {
    register,
    formState: { errors },
  } = useFormContext<SignupFormData>();

  const ErrorMessage = {
    pattern: i18n._(
      t({
        id: "optimised-signup.email.field-validation-warning",
        message: "Please enter a valid email address",
      })
    ),
    validate: i18n._(
      t({
        id: "optimised-signup.email.canonical-gocardless-warning",
        message:
          "Cannot create account using canonical GoCardless email. Use + to separate name ie.: jblack+test@gocardless.com",
      })
    ),
    required: i18n._(
      t({
        id: "optimised-signup.email.field-required-warning",
        message: "Email is required",
      })
    ),
  };

  useEffect(() => {
    if (errors.email?.message) {
      sendEvent(TrackingEvent.SIGNUP_EMAIL_FIELD_VALIDATION_FAILED, {
        label: errors.email?.message,
        error_origin: TrackingErrorOrigin.client_side,
      });
    }
  }, [errors.email, sendEvent]);

  return (
    <Field data-testid="email">
      <Label htmlFor="email">
        <Trans id="optimised-sign-up.form.email">Email</Trans>
      </Label>
      <Input
        type="email"
        id="email"
        status={getInputErrorStatus(!!errors.email)}
        autoComplete="on"
        {...register("email", {
          required: ErrorMessage.required,
          pattern: {
            value: VALID_EMAIL_REGEX,
            message: ErrorMessage.pattern,
          },
          validate: (value) =>
            !invalidGoCardlessEmailSignUp(value) || ErrorMessage.validate,
          onBlur: () => {
            sendEvent(TrackingEvent.SIGNUP_FORM_FIELD_INTERACTION, {
              label: "Email",
              type: "Blur",
            });
          },
        })}
      />
      {errors?.email && (
        <Hint status={FormFieldStatus.Danger}>{errors.email.message}</Hint>
      )}
    </Field>
  );
};
